import { PayloadAction } from '@reduxjs/toolkit';
import { ICartEntry } from 'features/cart/types';
import { isEqual, isNumber } from 'lodash-es';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { SYNC_WITH_STORAGE_ACTION } from 'state/global-state/global-actions';
import { createAppSlice } from 'state/global-state/utils';
import { CartEntryType } from 'utils/cart/types';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { ICartState } from './cart.types';

const getCartEntriesFromStorage = () => {
  const cartEntries = syncStorage.getObject<ILegacyCartEntry[]>(SyncStorageKey.CART);
  return (cartEntries || []) as ILegacyCartEntry[];
};

const setCartEntriesInStorage = (cartEntries: ILegacyCartEntry[]) => {
  syncStorage.setObject(SyncStorageKey.CART, cartEntries);
};

const getMinimalCartEntriesFromStorage = () => {
  const minimalCartEntries = syncStorage.getObject<ICartEntry[]>(SyncStorageKey.MINIMAL_CART);
  return (minimalCartEntries || []) as ICartEntry[];
};

const setMinimalCartEntriesInStorage = (minimalCartEntries: ICartEntry[]) => {
  syncStorage.setObject(SyncStorageKey.MINIMAL_CART, minimalCartEntries);
};

export const initialState: ICartState = {
  cartEntries: [],
  minimalCartEntries: [],
  isPricingOrder: false,
  pricedOrderId: null,
};

export const cartSlice = createAppSlice({
  name: 'cart',
  initialState,
  reducers: {
    addCartEntry: (state, { payload }: PayloadAction<ILegacyCartEntry>) => {
      // Safety measure to avoid adding duplicate entries,
      // there are some effects in the app that can trigger this action multiple times.
      if (state.cartEntries.find(entry => entry.cartId === payload.cartId)) {
        return;
      }
      state.cartEntries.push(payload);
      setCartEntriesInStorage(state.cartEntries);
    },
    addMinimalCartEntry: (state, { payload }: PayloadAction<ICartEntry>) => {
      state.minimalCartEntries.push(payload);
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    insertMinimalCartEntryAtIndex: (
      state,
      { payload }: PayloadAction<{ entry: ICartEntry; index: number }>
    ) => {
      const { entry, index } = payload;
      state.minimalCartEntries.splice(index, 0, entry);
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    replaceMinimalCartEntry: (
      state,
      { payload }: PayloadAction<{ oldCartId: string; newEntry: ICartEntry }>
    ) => {
      const { oldCartId, newEntry } = payload;
      const index = state.minimalCartEntries.findIndex(entry => entry.cartId === oldCartId);
      if (index !== -1) {
        // Replace in place to avoid flicker
        state.minimalCartEntries[index] = newEntry;
      } else {
        // If not found, add to the end
        state.minimalCartEntries.push(newEntry);
      }
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    removeMinimalCartEntry: (state, { payload }: PayloadAction<ICartEntry>) => {
      state.minimalCartEntries = state.minimalCartEntries.filter(
        entry => entry.cartId !== payload.cartId
      );
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    // used to remove single and multiple entries
    removeCartEntries: (state, { payload: idsToRemove }: PayloadAction<Set<string>>) => {
      state.cartEntries = state.cartEntries.filter(entry => !idsToRemove.has(entry.cartId));
      state.minimalCartEntries = state.minimalCartEntries.filter(
        entry => !(idsToRemove.has(entry.id) || idsToRemove.has(entry.cartId ?? ''))
      );

      setCartEntriesInStorage(state.cartEntries);
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    // FIXME: replace this action because is unsafe to delegate the responsibility of mutating the state to the consumers.
    repriceCartEntries: (state, { payload }: PayloadAction<ILegacyCartEntry[]>) => {
      // Keep the stale async safety check, but ignore the extra OfferDiscount entry
      // added during hydration. We compare only non-discount items so outdated
      // async results can't overwrite the current cart.
      const countWithoutDiscount = (entries: ILegacyCartEntry[]) =>
        entries.filter(entry => entry.type !== CartEntryType.offerDiscount).length;

      const payloadCount = countWithoutDiscount(payload);
      const stateCount = countWithoutDiscount(state.cartEntries);

      if (payloadCount !== stateCount) {
        return;
      }

      state.cartEntries = payload;
      setCartEntriesInStorage(payload);
    },
    resetCart: state => {
      state.cartEntries = [];
      setCartEntriesInStorage([]);
    },
    resetMinimalCart: state => {
      if (state.minimalCartEntries.length > 0) {
        state.minimalCartEntries = [];
      }
      setMinimalCartEntriesInStorage([]);
    },
    updateMinimalCartEntry: (
      state,
      { payload }: PayloadAction<{ cartEntry: ICartEntry; cartId: string }>
    ) => {
      const { cartEntry, cartId } = payload;
      state.minimalCartEntries = state.minimalCartEntries.map(entry =>
        entry.cartId === cartId ? cartEntry : entry
      );
      setMinimalCartEntriesInStorage(state.minimalCartEntries);
    },
    updateCartEntry: (
      state,
      { payload }: PayloadAction<{ cartEntry: ILegacyCartEntry; cartId: string }>
    ) => {
      const { cartEntry, cartId } = payload;
      const index = state.cartEntries.findIndex(entry => entry.cartId === cartId);
      state.cartEntries =
        index !== -1
          ? [...state.cartEntries.slice(0, index), cartEntry, ...state.cartEntries.slice(index + 1)]
          : [...state.cartEntries, cartEntry];
      setCartEntriesInStorage(state.cartEntries);
    },
    updateQuantityAndPrice: (
      state,
      { payload }: PayloadAction<{ cartId: string; quantity: number; price?: number }>
    ) => {
      const { cartId, quantity, price } = payload;
      state.cartEntries = state.cartEntries.map(entry => {
        if (entry.cartId === cartId) {
          entry.quantity = quantity;
          if (isNumber(price)) {
            entry.price = price;
          }
        }
        return entry;
      });
      setCartEntriesInStorage(state.cartEntries);
    },
    setIsPricingOrder: (state, { payload }: PayloadAction<boolean>) => {
      state.isPricingOrder = payload;
    },
    setPricedOrderId: (state, { payload }: PayloadAction<string | null>) => {
      state.pricedOrderId = payload;
    },
  },
  extraReducers: {
    [SYNC_WITH_STORAGE_ACTION]: (state: ICartState) => {
      const cartEntriesFromStorage = getCartEntriesFromStorage();
      if (!isEqual(state.cartEntries, cartEntriesFromStorage)) {
        state.cartEntries = cartEntriesFromStorage;
      }
      const minimalCartEntriesFromStorage = getMinimalCartEntriesFromStorage();
      if (!isEqual(state.minimalCartEntries, minimalCartEntriesFromStorage)) {
        state.minimalCartEntries = minimalCartEntriesFromStorage;
      }
    },
  },
});
